import React, { useEffect, useState } from "react";
import { Container, Link, styled } from "@mui/material";
import { KAHUN_PLATFORM, LOGIN_STAGE, LOGIN_STAGE_TITLES } from "./constants";
import { LoginForm } from "./loginForm";
import { SupportedLanguages } from "@kahunmedical/common-configuration/dist/configuration/supportedLanguages";
import { SignUpForm } from "./signUpForm.tsx";
import { NewPasswordForm } from "./newPasswordForm.tsx";
import { VerifyCodeWidget } from "./verifyCodeWidget.tsx";

//region [[ Styles ]]
const LoginWidgetView = styled(Container)({
  position: "relative",
  background: "#ffffff",
  boxShadow: "#8082827d -1px 1px 7px 1px",
  padding: "20px",
  borderRadius: 4,
});
//endregion [[ Styles ]]
export type LoginWidgetLoginProps = {
  onSignIn: (email?: string) => void;
  socialLoginRedirect?: string;
  enableGoogleLogin?: boolean;
  keepLoading?: boolean;
  onLoading?: () => void;
  onLoadingDone?: () => void;
};

export type LoginWidgetSignUpProps = {
  onSignUpComplete: (email?: string) => void;
  children?: React.ReactNode;
  requireFullName?: boolean;
  requireSpecialty?: boolean;
  requireTermsConfirm?: boolean;
  customFields?: Record<string, string>;
  customClientData?: Record<string, string>;
};

export type LoginWidgetNewPasswordProps = {
  onNewPasswordComplete: (email?: string) => void;
};

export type LoginWidgetVerifyEmailProps = {
  onComplete: () => void;
  code?: string;
  username?: string;
};

//region [[ Props ]]
export interface LoginWidgetProps {
  stage?: LOGIN_STAGE;
  message?: React.ReactNode;
  disabled?: boolean;
  locale?: string;
  platform?: KAHUN_PLATFORM;
  clinic?: string;
  onError?: (error: any) => void;
  showErrors?: boolean;
  trackEvents?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  loginFormProps?: LoginWidgetLoginProps;
  signUpFormProps?: LoginWidgetSignUpProps;
  newPasswordFormProps?: LoginWidgetNewPasswordProps;
  verifyEmailProps?: LoginWidgetVerifyEmailProps;
  onStageChange?: (stage: LOGIN_STAGE) => void;
  hideTitles?: boolean;
  className?: string;
  customContainer?: React.ElementType;
}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const LoginWidget = ({
  stage,
  loginFormProps,
  signUpFormProps,
  newPasswordFormProps,
  verifyEmailProps,
  onError,
  message,
  onStageChange,
  platform = KAHUN_PLATFORM.UNKNOWN,
  locale = SupportedLanguages.English_US,
  clinic,
  disabled,
  showErrors = true,
  trackEvents = true,
  maxWidth,
  hideTitles,
  className,
  customContainer,
}: LoginWidgetProps) => {
  const [currentStage, setCurrentStage] = useState(stage || LOGIN_STAGE.Login);

  useEffect(() => {
    setCurrentStage(stage || LOGIN_STAGE.Login);
  }, [stage]);

  const handleStageChange = (stage: LOGIN_STAGE) => {
    setCurrentStage(stage);
    onStageChange && onStageChange(stage);
  };

  const ContainerComponent = customContainer || LoginWidgetView;

  return (
    <ContainerComponent
      sx={{
        "& .login-widget-message": {
          margin: "10px 0",
          position: "relative",
        },
        "& .login-widget-footer": {
          marginTop: "20px",
        },
        "& .login-widget-title": {
          textAlign: "center",
          fontSize: "1.4em",
          marginTop: 0,
          marginBottom: "20px",
        },
        "& .navigation-link": {
          textAlign: "center",
          marginTop: "10px",
          fontSize: "0.88em",
          "& a": {
            cursor: "pointer",
          },
        },
      }}
      maxWidth={maxWidth || "sm"}
      className={"login-widget-view " + className}
    >
      {!hideTitles && (
        <h3 className={"login-widget-title"}>
          {LOGIN_STAGE_TITLES[currentStage]}
        </h3>
      )}
      {message && <div className={"login-widget-message"}>{message}</div>}
      {currentStage === LOGIN_STAGE.Login && (
        <LoginForm
          {...loginFormProps}
          onError={onError}
          disabled={disabled}
          platform={platform}
          locale={locale}
          clinic={clinic}
          trackEvents={trackEvents}
          showErrors={showErrors}
        />
      )}
      {currentStage === LOGIN_STAGE.Signup && (
        <SignUpForm
          {...signUpFormProps}
          onError={onError}
          platform={platform}
          locale={locale}
          clinic={clinic}
          trackEvents={trackEvents}
          showErrors={showErrors}
        />
      )}
      {currentStage === LOGIN_STAGE.ForgotPassword && (
        <NewPasswordForm
          {...newPasswordFormProps}
          onError={onError}
          showErrors={showErrors}
          trackEvents={trackEvents}
        />
      )}
      {currentStage === LOGIN_STAGE.VerifyEmail && (
        <VerifyCodeWidget {...verifyEmailProps} />
      )}

      <div className="login-widget-footer">
        {(currentStage === LOGIN_STAGE.Signup ||
          currentStage === LOGIN_STAGE.ForgotPassword ||
          currentStage === LOGIN_STAGE.VerifyEmail) && (
          <div className={"navigation-link login-link"}>
            Already have an account?{" "}
            <Link
              color={"primary"}
              onClick={() => handleStageChange(LOGIN_STAGE.Login)}
            >
              Login
            </Link>
          </div>
        )}

        {currentStage === LOGIN_STAGE.Login && (
          <div className={"navigation-link forgot-password-link"}>
            <Link
              color={"primary"}
              onClick={() => handleStageChange(LOGIN_STAGE.ForgotPassword)}
            >
              Forgot your password?
            </Link>
          </div>
        )}

        {(currentStage === LOGIN_STAGE.Login ||
          currentStage === LOGIN_STAGE.ForgotPassword) && (
          <div className={"navigation-link signup-link"}>
            Don't have a user yet?{" "}
            <Link
              color={"primary"}
              onClick={() => handleStageChange(LOGIN_STAGE.Signup)}
            >
              Sign Up
            </Link>
          </div>
        )}
      </div>
    </ContainerComponent>
  );
};
